/**
 * TOSS Web Application - TimeZoneClock Component
 *
 * Displays current time for multiple time zones in the header.
 * Shows Moscow, Novosibirsk, and Vladivostok time.
 * Hidden on mobile devices (< 768px).
 */

import { useState, useEffect, useRef } from 'react';

// ============================================================================
// Types
// ============================================================================

interface TimeZone {
  id: string;
  label: string;
  timezone: string;
}

// ============================================================================
// Constants
// ============================================================================

const TIME_ZONES: TimeZone[] = [
  { id: 'msk', label: 'МСК', timezone: 'Europe/Moscow' },
  { id: 'nsk', label: 'НСК', timezone: 'Asia/Novosibirsk' },
  { id: 'vld', label: 'ВЛД', timezone: 'Asia/Vladivostok' },
];

// ============================================================================
// Helper Functions
// ============================================================================

function formatTime(date: Date, timezone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: timezone,
  }).format(date);
}

function getTimesForZones(zones: TimeZone[]): Record<string, string> {
  const now = new Date();
  const times: Record<string, string> = {};
  for (const zone of zones) {
    times[zone.id] = formatTime(now, zone.timezone);
  }
  return times;
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * TimeZoneClock displays current time for Moscow, Novosibirsk, and Vladivostok.
 * Updates every minute. Hidden on mobile devices.
 *
 * @example
 * ```tsx
 * <TimeZoneClock />
 * // Renders: МСК 14:35 │ НСК 18:35 │ ВЛД 21:35
 * ```
 */
export function TimeZoneClock(): JSX.Element {
  const [times, setTimes] = useState(() => getTimesForZones(TIME_ZONES));
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Update immediately to sync with current minute
    setTimes(getTimesForZones(TIME_ZONES));

    // Calculate delay until next minute
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    // First update at the start of next minute, then tick every 60 s
    const initialTimeout = setTimeout(() => {
      setTimes(getTimesForZones(TIME_ZONES));

      intervalRef.current = setInterval(() => {
        setTimes(getTimesForZones(TIME_ZONES));
      }, 60000);
    }, msUntilNextMinute);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  return (
    <div className="hidden lg:flex items-center gap-3 text-xs text-[var(--color-text-secondary)]">
      {TIME_ZONES.map((zone, index) => (
        <div key={zone.id} className="flex items-center gap-3">
          {index > 0 && (
            <span className="text-[var(--color-border)]">│</span>
          )}
          <span className="whitespace-nowrap">
            <span className="font-medium">{zone.label}</span>{' '}
            <span className="tabular-nums">{times[zone.id]}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default TimeZoneClock;
